import { type ReactNode, useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useMe } from '../context/MeContext.js';
import { BrandMark } from './BrandMark.js';
import { CourseSwitcher } from './CourseSwitcher.js';
import { DemoDataRibbon } from '../components/DemoDataRibbon.js';
import { cn } from '../lib/utils';

declare const __APP_VERSION__: string;

const NAV_ITEMS = [
  { key: 'home', label: 'Overview', to: '' },
  { key: 'map', label: 'Course Map', to: 'map' },
  { key: 'satellite', label: 'Satellite', to: 'satellite' },
  { key: 'weather', label: 'Weather', to: 'weather' },
  { key: 'disease', label: 'Disease Risk', to: 'disease' },
  { key: 'briefing', label: 'Daily Briefing', to: 'briefing' },
  { key: 'photo', label: 'Photo AI', to: 'photo' },
  { key: 'sensors', label: 'Soil Sensors', to: 'sensors' },
  { key: 'irrigation', label: 'Irrigation', to: 'irrigation' },
  { key: 'chemical', label: 'Chemical & Inventory', to: 'chemical' },
  { key: 'compliance', label: 'Compliance', to: 'compliance' },
  { key: 'water', label: 'Water Quality', to: 'water' },
  { key: 'advisor', label: 'Turf Advisor', to: 'advisor' },
  { key: 'twin', label: 'Digital Twin', to: 'twin' },
  { key: 'ownership', label: 'Ownership', to: 'ownership' },
  { key: 'settings', label: 'Settings', to: 'settings' },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { me } = useMe();
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  // AppShell is mounted under the `/app/*` splat route, so React Router's
  // `useParams()` doesn't see `:courseSlug` — that param only exists on the
  // nested child routes inside this shell. Parse the pathname ourselves so
  // the sidebar actually renders. Returns undefined at `/app` (course
  // picker), which collapses the sidebar as intended.
  const courseSlug = (() => {
    const match = location.pathname.match(/^\/app\/([^/]+)/);
    return match?.[1];
  })();

  const course = me?.courses.find((c) => c.courseSlug === courseSlug) ?? null;

  // Auto-close sidebar on route change (mobile)
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen flex-col bg-bg-page text-fg">
      <DemoDataRibbon />
      <header
        className={cn(
          'sticky top-0 z-50 flex h-16 items-center gap-4 px-4 md:px-6',
          'border-b border-border bg-nav-blur'
        )}
      >
        <button
          onClick={() => setNavOpen((v) => !v)}
          className={cn(
            'lg:hidden inline-flex size-9 items-center justify-center rounded-sm',
            'text-fg transition-colors hover:bg-bg-hover',
            'focus-visible:outline-none focus-visible:shadow-focus'
          )}
          aria-label="Toggle navigation"
          aria-expanded={navOpen}
        >
          {navOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
        <Link to="/app" className="flex items-center gap-2.5">
          <BrandMark className="h-8 w-8" />
          <span className="font-display text-xl font-bold tracking-tight text-fg">
            Green<span className="text-greeniq-400">IQ</span>
            <span className="text-greeniq-500">.</span>
          </span>
        </Link>
        <div className="flex-1" />
        {courseSlug && course && <CourseSwitcher activeCourseSlug={courseSlug} />}
        <span
          className="hidden sm:inline font-mono text-[10px] text-fg-muted/80 tracking-wider tabular-nums"
          title={`Build ${__APP_VERSION__}`}
        >
          v{__APP_VERSION__}
        </span>
      </header>

      <div className="flex flex-1">
        {courseSlug && (
          <>
            {navOpen && (
              <button
                className="fixed inset-0 top-16 z-30 bg-black/60 lg:hidden"
                onClick={() => setNavOpen(false)}
                aria-label="Close navigation"
              />
            )}
            <aside
              className={cn(
                'w-60 shrink-0 overflow-y-auto border-r border-border bg-bg-card',
                'fixed inset-y-0 left-0 top-16 z-40 lg:static lg:top-0',
                'transition-transform duration-[250ms] ease-out',
                navOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
              )}
            >
              <nav className="flex flex-col gap-0.5 p-3">
                {NAV_ITEMS.map((item) => (
                  <NavLink
                    key={item.key}
                    to={item.to === '' ? `/app/${courseSlug}` : `/app/${courseSlug}/${item.to}`}
                    end={item.to === ''}
                    className={({ isActive }) =>
                      cn(
                        'relative block rounded-sm px-3 py-2 text-sm font-medium transition-colors duration-[150ms]',
                        'focus-visible:outline-none focus-visible:shadow-focus',
                        isActive
                          ? 'bg-greeniq-500/10 text-greeniq-300 ring-1 ring-inset ring-greeniq-500/30 shadow-glow-green'
                          : 'text-fg-secondary hover:bg-bg-hover hover:text-fg'
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
              <div className="sticky bottom-0 border-t border-border bg-bg-card p-3 text-[10px] uppercase tracking-[0.12em] text-fg-muted">
                {course?.courseName ?? 'GreenIQ'}
              </div>
            </aside>
          </>
        )}

        <main className="relative flex-1 min-w-0">
          {/* Ambient green glow behind content */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-[360px] blur-3xl"
            style={{
              background:
                'radial-gradient(ellipse at center, rgba(34, 212, 122, 0.10) 0%, rgba(34, 212, 122, 0.04) 40%, transparent 70%)',
            }}
          />
          <div className="relative">{children}</div>
        </main>
      </div>
    </div>
  );
}
